import React, { useMemo, useState, useCallback } from 'react';
import styled from 'styled-components';
import arrowDown from '../../../static/img/arrowdown2.svg';
import arrowUp from '../../../static/img/arrowup2.svg';
import { CHECK_PAGES_COPY } from '../../ScriptIngestion/CheckPages/copy';

const getClassLabel = (clazz) => clazz.name || clazz.title || 'Class';
const getStudentId = (student) => student.id ?? student.studentId;
const getStudentLabel = (student) =>
  student.name || student.studentName || 'Student';

const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
    <path
      d="M2.5 6L5 8.5L9.5 3.5"
      stroke="#fff"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Class + student picker used on Organise (Figma: Add students hierarchical list). */
export const BulkUploadClassStudentFilter = ({
  classes = [],
  selectedClassIds = [],
  selectedStudentIds = [],
  onClassIdsChange,
  onStudentIdsChange,
}) => {
  const [expandedClassIds, setExpandedClassIds] = useState(() => new Set());

  const allStudentIds = useMemo(() => {
    const ids = [];
    (classes || []).forEach((clazz) => {
      (clazz.students || []).forEach((student) => {
        const id = getStudentId(student);
        if (id) ids.push(id);
      });
    });
    return ids;
  }, [classes]);

  const selectedClassSet = useMemo(
    () => new Set((selectedClassIds || []).map(String)),
    [selectedClassIds],
  );
  const selectedStudentSet = useMemo(
    () => new Set((selectedStudentIds || []).map(String)),
    [selectedStudentIds],
  );

  const isStudentSelected = useCallback(
    (id) => selectedStudentSet.has(String(id)),
    [selectedStudentSet],
  );

  const getClassStudentIds = useCallback(
    (clazz) =>
      (clazz.students || [])
        .map(getStudentId)
        .filter(Boolean),
    [],
  );

  const isClassFullySelected = useCallback(
    (clazz) => {
      const studentIds = getClassStudentIds(clazz);
      if (!studentIds.length) return selectedClassSet.has(String(clazz.id));
      return studentIds.every((id) => selectedStudentSet.has(String(id)));
    },
    [getClassStudentIds, selectedClassSet, selectedStudentSet],
  );

  const isAllSelected =
    classes.length > 0 &&
    classes.every((clazz) => selectedClassSet.has(String(clazz.id)));

  // Helper function - not used anymore but kept for backward compatibility
  const syncClassIdsFromStudents = useCallback(
    (nextStudentIds) => {
      const studentSet = new Set(nextStudentIds.map(String));
      const nextClassIds = (classes || [])
        .filter((clazz) => {
          const ids = getClassStudentIds(clazz);
          return ids.length > 0 && ids.every((id) => studentSet.has(String(id)));
        })
        .map((clazz) => clazz.id);
      onClassIdsChange?.(nextClassIds);
      onStudentIdsChange?.(nextStudentIds);
    },
    [classes, getClassStudentIds, onClassIdsChange, onStudentIdsChange],
  );

  const handleToggleAll = () => {
    if (isAllSelected) {
      onClassIdsChange?.([]);
      onStudentIdsChange?.([]);
      return;
    }
    onClassIdsChange?.((classes || []).map((clazz) => clazz.id));
    onStudentIdsChange?.([...allStudentIds]);
  };

  const handleToggleClass = (clazz) => {
    const studentIds = getClassStudentIds(clazz);
    const isSelected = selectedClassSet.has(String(clazz.id));

    if (!studentIds.length) {
      // Class has no students - just toggle class ID directly
      if (isSelected) {
        onClassIdsChange?.(
          (selectedClassIds || []).filter((id) => String(id) !== String(clazz.id)),
        );
      } else {
        onClassIdsChange?.([...(selectedClassIds || []), clazz.id]);
      }
      return;
    }

    if (isSelected) {
      // Deselect this class's students
      const remove = new Set(studentIds.map(String));
      const remainingStudents = (selectedStudentIds || []).filter((id) => !remove.has(String(id)));

      // Remove this class from selectedClassIds
      const remainingClassIds = (selectedClassIds || []).filter(
        (id) => String(id) !== String(clazz.id)
      );

      onClassIdsChange?.(remainingClassIds);
      onStudentIdsChange?.(remainingStudents);
      return;
    }

    // Select this class's students (keep other selected students)
    const merged = [
      ...(selectedStudentIds || []),
      ...studentIds.filter((id) => !selectedStudentSet.has(String(id))),
    ];

    // Add this class to selectedClassIds
    const updatedClassIds = [
      ...(selectedClassIds || []),
      clazz.id,
    ];

    onClassIdsChange?.(updatedClassIds);
    onStudentIdsChange?.(merged);
  };

  const handleToggleStudent = (studentId, clazz) => {
    const idStr = String(studentId);

    if (isStudentSelected(studentId)) {
      // Deselect student
      const remainingStudents = (selectedStudentIds || []).filter((id) => String(id) !== idStr);

      // Check if parent class should be deselected
      const classStudentIds = getClassStudentIds(clazz);
      const remainingSet = new Set(remainingStudents.map(String));
      const classStillFullySelected =
        classStudentIds.length > 0 &&
        classStudentIds.every((id) =>
          String(id) === idStr ? false : remainingSet.has(String(id)),
        );

      const updatedClassIds = classStillFullySelected
        ? selectedClassIds
        : (selectedClassIds || []).filter((id) => String(id) !== String(clazz.id));

      onClassIdsChange?.(updatedClassIds);
      onStudentIdsChange?.(remainingStudents);
      return;
    }

    // Select student
    const updatedStudents = [...(selectedStudentIds || []), studentId];

    // Check if parent class is now fully selected
    const classStudentIds = getClassStudentIds(clazz);
    const updatedSet = new Set(updatedStudents.map(String));
    const classNowFullySelected =
      classStudentIds.length > 0 &&
      classStudentIds.every((id) => updatedSet.has(String(id)));

    const updatedClassIds = classNowFullySelected
      ? [...(selectedClassIds || []), clazz.id].filter((id, idx, arr) => arr.indexOf(id) === idx) // Unique
      : selectedClassIds;

    onClassIdsChange?.(updatedClassIds);
    onStudentIdsChange?.(updatedStudents);

    // Auto-expand parent when selecting a student
    setExpandedClassIds((prev) => {
      if (prev.has(clazz.id)) return prev;
      const next = new Set(prev);
      next.add(clazz.id);
      return next;
    });
  };

  const toggleExpanded = (classId) => {
    setExpandedClassIds((prev) => {
      const next = new Set(prev);
      if (next.has(classId)) next.delete(classId);
      else next.add(classId);
      return next;
    });
  };

  return (
    <Wrap>
      <Heading>Add students</Heading>
      <Hint>Select classes and individual students</Hint>
      <List>
        <Row
          type="button"
          $selected={isAllSelected}
          onClick={handleToggleAll}
        >
          <CheckboxVisual $checked={isAllSelected} aria-hidden>
            {isAllSelected && <CheckIcon />}
          </CheckboxVisual>
          <RowLabel $selected={isAllSelected}>All</RowLabel>
        </Row>

        {(classes || []).map((clazz) => {
          const classSelected = selectedClassSet.has(String(clazz.id));
          const expanded = expandedClassIds.has(clazz.id);
          const students = clazz.students || [];

          return (
            <ClassBlock key={clazz.id}>
              <Row
                type="button"
                $selected={classSelected}
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleClass(clazz);
                }}
              >
                <CheckboxVisual $checked={classSelected} aria-hidden>
                  {classSelected && <CheckIcon />}
                </CheckboxVisual>
                <RowLabel $selected={classSelected}>{getClassLabel(clazz)}</RowLabel>
                <ChevronButton
                  type="button"
                  aria-label={expanded ? 'Collapse class' : 'Expand class'}
                  aria-expanded={expanded}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpanded(clazz.id);
                  }}
                >
                  <img src={expanded ? arrowUp : arrowDown} alt="" width={16} height={16} />
                </ChevronButton>
              </Row>

              {expanded && students.length > 0 && (
                <StudentList>
                  {students.map((student) => {
                    const studentId = getStudentId(student);
                    if (!studentId) return null;
                    const checked = isStudentSelected(studentId);
                    return (
                      <StudentRow
                        key={studentId}
                        type="button"
                        onClick={() => handleToggleStudent(studentId, clazz)}
                      >
                        <CheckboxVisual $checked={checked} aria-hidden>
                          {checked && <CheckIcon />}
                        </CheckboxVisual>
                        <RowLabel>
                          {getStudentLabel(student)}
                          {/* 06 §5.8: the upload becomes this student's resubmission. */}
                          {student.resubmission && (
                            <RowNote>{` · ${CHECK_PAGES_COPY.resubmissionRequested}`}</RowNote>
                          )}
                        </RowLabel>
                      </StudentRow>
                    );
                  })}
                </StudentList>
              )}
            </ClassBlock>
          );
        })}
      </List>
    </Wrap>
  );
};

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid rgba(201, 198, 204, 0.55);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`;

const Heading = styled.h3`
  margin: 0;
  font-family: var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif);
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: #1e252a;
`;

const Hint = styled.p`
  margin: 0 0 4px;
  font-family: var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif);
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #918b97;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 320px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #7200e0 transparent;
`;

const ClassBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Row = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 40px;
  padding: 8px 10px;
  border: none;
  border-radius: 8px;
  background: ${({ $selected }) => ($selected ? 'rgba(241, 230, 252, 0.85)' : 'transparent')};
  cursor: pointer;
  text-align: left;
  box-sizing: border-box;

  &:hover {
    background: ${({ $selected }) =>
      $selected ? 'rgba(241, 230, 252, 0.95)' : 'rgba(241, 230, 252, 0.35)'};
  }
`;

const StudentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 0 0 4px 28px;
`;

const StudentRow = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 36px;
  padding: 6px 10px;
  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  text-align: left;
  box-sizing: border-box;

  &:hover {
    background: rgba(241, 230, 252, 0.35);
  }
`;

const CheckboxVisual = styled.span`
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  border-radius: 4px;
  border: 1.5px solid ${({ $checked }) => ($checked ? '#7200e0' : '#c9c6cc')};
  background: ${({ $checked }) => ($checked ? '#7200e0' : '#ffffff')};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
`;

const RowLabel = styled.span`
  flex: 1;
  min-width: 0;
  font-family: var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif);
  font-size: 14px;
  font-weight: ${({ $selected }) => ($selected ? 600 : 400)};
  line-height: 20px;
  color: ${({ $selected }) => ($selected ? '#7200e0' : '#1e252a')};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RowNote = styled.span`
  font-size: 12px;
  color: #4b464f;
`;

const ChevronButton = styled.button`
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  border: none;
  background: transparent;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;

  &:hover {
    background: rgba(114, 0, 224, 0.08);
  }

  img {
    display: block;
  }
`;
